import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { LogIn, ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Separator } from '../ui/separator';
import { getApiUrl } from '../../utils/api';
import { DeviceTrust } from '../../utils/deviceTrust';
import { LANGUAGES } from '../../constants/languages';
import BabelmixLogo from '../BabelmixLogo';
import OTPVerificationModal from '../OTPVerificationModal';

const Registration = ({ onUserRegistered, showLoginOption, onBackToLanding }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    phone_number: '',
    language: 'de',
    company: ''
  });
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(false);
  
  // OTP Verification State
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);
  const [deviceInfo, setDeviceInfo] = useState({});

  useEffect(() => {
    // Check for invite code in URL
    const urlParams = new URLSearchParams(window.location.search);
    const invite = urlParams.get('invite');
    if (invite) {
      setInviteCode(invite);
    }
    
    // Generate device fingerprint for user recognition
    const generateDeviceFingerprint = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Device fingerprint', 2, 2);
      
      const fingerprint = btoa(JSON.stringify({
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        screen: `${screen.width}x${screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        canvas: canvas.toDataURL()
      }));
      
      localStorage.setItem('deviceFingerprint', fingerprint);
      return fingerprint;
    };
    
    // Check if returning user based on device fingerprint
    const existingFingerprint = localStorage.getItem('deviceFingerprint');
    const existingUser = localStorage.getItem('currentUser');
    
    if (existingFingerprint && existingUser) {
      // Auto-login returning user
      try {
        const user = JSON.parse(existingUser);
        if (user && user.id) {
          console.log('🔐 Auto-login for returning user on known device');
          onUserRegistered(user);
          return;
        }
      } catch (error) {
        console.error('Error parsing existing user:', error);
      }
    }
    
    // Generate fingerprint for new device
    if (!existingFingerprint) {
      generateDeviceFingerprint();
    }
  }, [onUserRegistered]);

  // Phone number formatting helper
  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters except +
    let cleaned = value.replace(/[^\d+]/g, '');
    
    // Convert 00 at start to +
    if (cleaned.startsWith('00')) {
      cleaned = '+' + cleaned.substring(2);
    }
    
    // Ensure it starts with +
    if (cleaned && !cleaned.startsWith('+') && cleaned.length > 0) {
      // If starts with 0, assume German number
      if (cleaned.startsWith('0')) {
        cleaned = '+49' + cleaned.substring(1);
      }
    }
    
    return cleaned;
  };

  // Phone number validation
  const validatePhoneNumber = (phone) => {
    if (!phone) return { valid: false, message: t('auth.phoneRequired', 'Phone number is required') };
    if (!phone.startsWith('+')) return { valid: false, message: t('auth.useInternationalFormat', 'Please use international format (e.g. +49...)') };
    if (phone.length < 10) return { valid: false, message: t('auth.phoneTooShort', 'Phone number is too short') };
    if (phone.length > 16) return { valid: false, message: t('auth.phoneTooLong', 'Phone number is too long') };
    return { valid: true, message: '' };
  };

  // Get country from phone number
  const getCountryFromPhone = (phone) => {
    const prefixes = {
      '+49': '🇩🇪 Deutschland',
      '+43': '🇦🇹 Österreich', 
      '+41': '🇨🇭 Schweiz',
      '+48': '🇵🇱 Polen',
      '+40': '🇷🇴 Rumänien',
      '+90': '🇹🇷 Türkei',
      '+355': '🇦🇱 Albanien',
      '+380': '🇺🇦 Ukraine',
      '+7': '🇷🇺 Russland',
      '+1': '🇺🇸 USA/Kanada',
      '+44': '🇬🇧 UK'
    };
    
    for (const [prefix, country] of Object.entries(prefixes)) {
      if (phone.startsWith(prefix)) return country;
    }
    return null;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData({...formData, phone_number: formatted});
  };

  const phoneValidation = validatePhoneNumber(formData.phone_number);
  const detectedCountry = getCountryFromPhone(formData.phone_number);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Validate required fields
    if (!formData.name.trim() || !formData.phone_number.trim() || !formData.company.trim()) {
      alert(t('errors.unknown'));
      setLoading(false);
      return;
    }
    
    // Validate phone number format
    const phoneCheck = validatePhoneNumber(formData.phone_number);
    if (!phoneCheck.valid) {
      alert(phoneCheck.message);
      setLoading(false);
      return;
    }

    try {
      console.log('Registering user with data:', formData);
      
      // Add device trust token to registration
      const deviceInfoData = DeviceTrust.getDeviceInfo ? DeviceTrust.getDeviceInfo() : {};
      setDeviceInfo(deviceInfoData);
      
      const registrationData = {
        ...formData,
        ...deviceInfoData
      };
      
      // DEMO FIX: Timeout für Server-Response hinzufügen  
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error(t('errors.serverError'))), 15000)
      );
      
      const registrationPromise = axios.post(`${getApiUrl()}/users`, registrationData);
      
      // Race zwischen Request und Timeout
      const response = await Promise.race([registrationPromise, timeoutPromise]);
      const user = response.data;
      console.log('Registration successful:', user);

      // If there's an invite code, try to join the project
      if (inviteCode) {
        try {
          await axios.post(`${getApiUrl()}/invites/${inviteCode}/join?user_id=${user.id}`);
          console.log('Successfully joined project with invite code:', inviteCode);
        } catch (inviteError) {
          console.error('Error processing invite:', inviteError);
          alert(t('errors.unknown'));
        }
      }

      // Check if SMS verification is enabled
      try {
        const smsStatus = await axios.get(`${getApiUrl()}/auth/sms-status`);
        if (smsStatus.data.sms_enabled) {
          // Show OTP verification modal
          setPendingUser(user);
          setShowOTPModal(true);
          return; // Don't complete registration yet
        }
      } catch (err) {
        console.log('SMS status check failed, proceeding without OTP');
      }

      // Save to localStorage and notify parent (if SMS not enabled)
      localStorage.setItem('currentUser', JSON.stringify(user));
      console.log('User saved to localStorage');
      onUserRegistered(user);
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = error.response?.data?.detail || t('errors.unknown');
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle OTP verification success
  const handleOTPVerified = (result) => {
    console.log('OTP verified:', result);
    if (pendingUser) {
      // Mark user as verified and save
      const verifiedUser = { ...pendingUser, phone_verified: true };
      localStorage.setItem('currentUser', JSON.stringify(verifiedUser));
      console.log('Verified user saved to localStorage');
      setShowOTPModal(false);
      onUserRegistered(verifiedUser);
    }
  };
  
  // Handle OTP modal close (cancel verification)
  const handleOTPClose = () => {
    // Still allow user in but mark as unverified
    if (pendingUser) {
      localStorage.setItem('currentUser', JSON.stringify(pendingUser));
      console.log('Unverified user saved to localStorage');
      setShowOTPModal(false);
      onUserRegistered(pendingUser);
    }
  };

  return (
    <div 
      className="h-full bg-gradient-to-br from-emerald-50 to-blue-50 flex items-start justify-center p-2 md:p-4 pt-2 overflow-y-auto" 
      style={{ height: '100%', minHeight: '100%' }}
    >
      <Card className="w-full max-w-md my-0.5 md:my-2 relative">
        {/* Back button */}
        {onBackToLanding && (
          <button
            onClick={onBackToLanding}
            className="absolute top-3 left-3 flex items-center gap-1 text-gray-500 hover:text-gray-700 text-sm z-10"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common.back')}
          </button>
        )}
        <CardHeader className="text-center py-1.5 md:py-3 pb-2">
          <div className="flex justify-center mb-0.5">
            <BabelmixLogo size="small" />
          </div>
          <p className="text-[10px] md:text-xs text-gray-600 leading-tight">{t('auth.createAccount')}</p>
        </CardHeader>
        <CardContent className="px-3 md:px-6 py-1.5 md:py-3">
          {inviteCode && (
            <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-sm text-emerald-700">
                <code>{inviteCode}</code>
              </p>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-1.5 md:space-y-3">
            {/* Name */}
            <div>
              <label className="block text-[10px] md:text-xs font-medium text-gray-700 mb-0.5">
                {t('profile.name')} *
              </label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder={t('profile.name')}
                disabled={loading}
                required
                className="text-xs md:text-sm h-8 md:h-10"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-[10px] md:text-xs font-medium text-gray-700 mb-0.5">
                {t('auth.phoneNumber')} *
              </label>
              <Input
                type="tel"
                value={formData.phone_number}
                onChange={handlePhoneChange}
                placeholder="+49 151 23456789"
                disabled={loading}
                required
                className={`text-xs md:text-sm h-8 md:h-10 ${!phoneValidation.valid && formData.phone_number ? 'border-red-500' : ''}`}
              />
              {formData.phone_number && (
                <div className="mt-1">
                  {detectedCountry && (
                    <span className="text-[10px] text-emerald-600">{detectedCountry}</span>
                  )}
                  {!phoneValidation.valid && (
                    <span className="text-[10px] text-red-500 ml-2">{phoneValidation.message}</span>
                  )}
                </div>
              )}
            </div>

            {/* Company */}
            <div>
              <label className="block text-[10px] md:text-xs font-medium text-gray-700 mb-0.5">
                {t('profile.company')} *
              </label>
              <Input
                type="text"
                value={formData.company}
                onChange={(e) => setFormData({...formData, company: e.target.value})}
                placeholder={t('auth.companyPlaceholder')}
                disabled={loading}
                required
                className="text-xs md:text-sm h-8 md:h-10"
              />
            </div>

            {/* Language */}
            <div>
              <label className="block text-[10px] md:text-xs font-medium text-gray-700 mb-0.5">
                {t('profile.language')}
              </label>
              <select
                value={formData.language}
                onChange={(e) => setFormData({...formData, language: e.target.value})}
                className="w-full p-1.5 md:p-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 text-xs md:text-sm h-8 md:h-10"
                disabled={loading}
              >
                {LANGUAGES.map(lang => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Optional: Project Invite Code */}
            <div className="pt-2 mt-2 border-t border-gray-200">
              <details className="group">
                <summary className="flex items-center gap-2 cursor-pointer text-[10px] md:text-xs text-gray-600 hover:text-emerald-700">
                  <span className="text-emerald-600">🎫</span>
                  <span>{t('auth.haveInviteCode', 'Have you received an invite code?')}</span>
                  <span className="ml-auto text-gray-400 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="mt-2 p-2 bg-gray-50 rounded-lg">
                  <p className="text-[9px] md:text-[10px] text-gray-500 mb-2">
                    {t('auth.inviteCodeExplanation', 'If a project leader has sent you an invite code, enter it here. After registration, you will automatically be added to their project. Without a code, you can create your own projects.')}
                  </p>
                  <Input
                    type="text"
                    placeholder={t('auth.projectCodePlaceholder', 'e.g. ABC123')}
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                    className="w-full text-center font-mono tracking-widest uppercase text-xs md:text-sm h-8 md:h-10"
                    maxLength="8"
                  />
                </div>
              </details>
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-emerald-600 hover:bg-emerald-700 py-2 text-sm md:text-base mt-1"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-3 w-3 md:h-4 md:w-4 border-b-2 border-white mr-2"></div>
                  <span className="text-xs md:text-sm">{t('common.loading')}</span>
                </div>
              ) : (
                t('auth.createProfile')
              )}
            </Button>
          </form>
          
          {/* DEMO MODE - Schneller Zugang für Präsentationen */}
          <div className="text-center mt-1.5">
            <button
              type="button"
              onClick={() => {
                const demoUser = {
                  id: 'demo-' + Date.now(),
                  name: 'Demo Benutzer',
                  phone_number: '+49151123456',
                  company: 'Demo GmbH',
                  language: 'de'
                };
                localStorage.setItem('currentUser', JSON.stringify(demoUser));
                onUserRegistered(demoUser);
              }}
              className="text-xs text-blue-600 hover:text-blue-800 underline"
              style={{fontSize: '9px', opacity: 0.5}}
            >
              🎭 Demo
            </button>
          </div>

          {showLoginOption && (
            <div className="text-center">
              <Separator className="my-1 md:my-2" />
              <p className="text-[9px] md:text-xs text-gray-600 mb-0.5 md:mb-1">{t('auth.alreadyHaveAccount')}</p>
              <Button
                variant="outline"
                onClick={showLoginOption}
                className="w-full text-xs md:text-sm py-1 md:py-2 h-7 md:h-10"
              >
                <LogIn className="w-3 h-3 mr-1" />
                {t('auth.login')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* OTP Verification Modal */}
      <OTPVerificationModal
        isOpen={showOTPModal}
        onClose={handleOTPClose}
        phoneNumber={formData.phone_number}
        deviceToken={deviceInfo.device_token}
        deviceType={deviceInfo.device_type}
        deviceName={deviceInfo.device_name}
        onVerified={handleOTPVerified}
        mode="registration"
      />
    </div>
  );
};

export default Registration;
